/**
 * lib/image-url.ts — Phase 3 single source of truth for image URL output.
 *
 * Two surfaces, two functions:
 *
 * 1. getImageDisplayUrl(supabaseUrl) — for in-page <Image> components.
 *    Returns the raw Supabase Storage URL because Next.js's <Image> component
 *    pipes EVERY src through `/_next/image?url=...` automatically (configured
 *    in next.config.js). The user-visible URL ends up on fridge.com regardless
 *    of what we pass here.
 *
 * 2. getImagePublicUrl(supabaseUrl, opts) — for OG meta tags + schema.org
 *    JSON-LD `image` fields + `<link rel="preload">` and other contexts where
 *    the URL is consumed by EXTERNAL parsers (Facebook share preview, Twitter
 *    cards, Google's structured-data parser, AI crawlers).
 *
 *    These contexts don't go through Next.js's <Image> wrapping, so a raw
 *    `*.supabase.co/storage/...` URL would leak — the share preview would
 *    show "supabase.co" as the host, the schema.org data would point off-
 *    domain, and Google's crawler would record the image as third-party.
 *
 *    This function rewrites the URL to fridge.com's `_next/image` endpoint,
 *    which serves the same image but on our domain with our optimization.
 *
 * Per master mission Rule 4 ("everything displayed on Fridge.com renders
 * from Supabase"): no raw vendor-CDN URLs leak in any context an external
 * party can see.
 *
 * Future-proofs us: if we ever leave Supabase Storage, swap the implementation
 * here and every caller stays correct. Same outcome as setting up an
 * `images.fridge.com` CNAME on Supabase Storage, but with zero DNS / SSL /
 * cache-invalidation overhead.
 */

import { correctProductLead } from './product-lead-images'
import { amazonOriginImages } from './product-images/amazon-image-licence'
import { dropExcludedProductImagesRuntime as dropExcludedProductImages } from './product-image-exclusion-runtime'
import { dropExcludedProductSourceImages } from './product-image-source-exclusions'
import {
  getProxiedStoragePath,
  isRenderableImageUrl,
  parseProxiedStorageUrl,
  PROXIED_STORAGE_BUCKETS,
} from './proxied-storage-path'

export { getProxiedStoragePath, isRenderableImageUrl, PROXIED_STORAGE_BUCKETS } from './proxied-storage-path'

const FRIDGE_HOST = 'https://fridge.com';
const STORAGE_PUBLIC_MARKER = '/storage/v1/object/public/';

/**
 * Supabase Storage buckets that are proxied through a fridge.com path by a
 * next.config.js rewrite (`/<bucket>/:path*` → Storage). Keep this list in
 * lockstep with `brandStorageBuckets` in next.config.js. A storage URL for one
 * of these buckets can be served as `fridge.com/<bucket>/<path>` — no raw
 * `*.supabase.co` host ever reaches the browser (owner directive 07/20).
 *
 * `product-images` joined the CDN lane 07/20: product photos now emit the
 * site-relative `/product-images/<path>` on every display surface, so no raw
 * `*.supabase.co` host leaks — not in the `<img>` src, the srcset, or the
 * `/_next/image?url=` query param. The bytes are served by the next.config
 * rewrite (byte-identical to the direct Storage object).
 *
 * EXPORTED so the "keep this in lockstep" sentence above is a checked claim and
 * not a hope: `tests/proxied-storage-buckets-in-sync.test.ts` pins this list
 * equal to next.config.js's `brandStorageBuckets` as a SET, and drives Next's
 * real matcher over the `?v=` path this module emits for every bucket in it.
 */
/** The two image columns every product row carries. */
export interface ProductImageColumns {
  images?: string[] | null
  images_supabase?: string[] | null
}

/**
 * Order rows so cards that can be ILLUSTRATED come first, then apply the
 * caller's own tie-break.
 *
 * FRIDGE-398 step 1 / FRIDGE-387: a rail must never feature a card it cannot
 * illustrate. `lib/recommendations.ts` owns that rule for the ~15 rails fed by
 * its shared selector; this is the same rule for surfaces that sort their own
 * rows and keep their own ordering — the brand page's two carousels, 12 slots
 * each, previously ordered by `lowest_price_cents` and nothing else.
 *
 * It lives here rather than in recommendations.ts because those callers do not
 * carry `RecommendedProduct` — the only thing this needs is the image columns.
 *
 * RANKS, NEVER DROPS. A short rail hides a catalogue problem; an un-illustrated
 * card ranked last surfaces it. Same call `lib/parts/featured.ts` makes.
 *
 * The gate is `isRenderableImageUrl(pickProductImage(row))`, so it honours the
 * `images_supabase[0] ?? images[0]` precedence and accepts the site-relative
 * proxied paths a mirrored image emits. It is deliberately NOT a query-level
 * `.not('images','is',null)`: in Postgres an empty `text[]` is not NULL, so that
 * predicate admits `{}` and dead URLs alike. Measured 08/22/2026 across live
 * PDPs — of the 9 rows with no image, **1** is NULL and **8** are empty arrays,
 * so the query filter admits eight of the nine it is believed to exclude.
 */
export function rankIllustratedFirst<T extends ProductImageColumns>(
  rows: readonly T[],
  tieBreak: (a: T, b: T) => number
): T[] {
  const illustrated = (r: T) => (isRenderableImageUrl(pickProductImage(r)) ? 1 : 0)
  return [...rows].sort((a, b) => illustrated(b) - illustrated(a) || tieBreak(a, b))
}

/**
 * How many rows to fetch when a rail wants `n` — see `takeIllustratedFirst`.
 *
 * THREE, not two, and not "a few more". A rail that wants 6 and fetches 6 has no
 * spare illustrated row to promote, so ranking is a no-op. 3x gives the ranking
 * something to choose from at every realistic miss rate: live un-illustrable
 * stock is 0.18% (9 of 5,139 on 08/23/2026), so 18 rows carry a vanishing chance
 * of yielding fewer than 6 illustrated ones — while staying small enough that no
 * rail turns into a table scan.
 */
export const RAIL_OVERFETCH = 3

/**
 * Take `n` rows, illustrated first — the whole rail rule in one call.
 *
 * WHY THIS EXISTS RATHER THAN JUST SORTING. Nine calculator/tool blocks each ran
 * `.limit(6)` and rendered the result directly. Sorting that is pointless: the
 * database already truncated to 6, so if one of those 6 has no photo the rail
 * shows a placeholder no matter how it is ordered. The fix has to happen at
 * FETCH time — ask for more than you need, then rank, then slice. That is the
 * difference between a sort key and an actual guarantee, and it is the half that
 * `postProcess` in lib/recommendations.ts could not fix for callers that issue
 * their own queries.
 *
 * RANKS, NEVER DROPS (see rankIllustratedFirst): if the pool genuinely cannot
 * fill `n` with illustrated rows, the rail still renders `n` cards with the
 * un-illustrated ones last, rather than silently shortening and hiding a
 * catalogue problem.
 *
 * Pair with `.limit(n * RAIL_OVERFETCH)` on the query.
 */
export function takeIllustratedFirst<T extends ProductImageColumns>(
  rows: readonly T[],
  n: number,
  tieBreak: (a: T, b: T) => number = () => 0
): T[] {
  return rankIllustratedFirst(rows, tieBreak).slice(0, n)
}

/** True when `url` is a Supabase Storage public-object URL (any bucket, any project ref). */
function isStorageObjectUrl(url: string): boolean {
  return url.indexOf(STORAGE_PUBLIC_MARKER) !== -1;
}

/** Non-empty strings only, first occurrence wins — an image never appears twice. */
function cleanImageList(list: unknown): string[] {
  if (!Array.isArray(list)) return [];
  const out: string[] = [];
  const seen = new Set<string>();
  for (const entry of list) {
    if (typeof entry !== 'string') continue;
    const trimmed = entry.trim();
    if (!trimmed || seen.has(trimmed)) continue;
    seen.add(trimmed);
    out.push(trimmed);
  }
  return out;
}

export interface ResolveCanonicalImagesOptions {
  /**
   * FRIDGE-1127 — the PROVEN-dead image URLs, as built by
   * `lib/seo/known-dead-images.ts` from the OPEN `broken_image_*` rows the
   * qa-broken-images detector files (HEAD + plain-GET re-check, one row per
   * URL, closed the moment the image heals). When a set is supplied those URLs
   * leave the precedence at exactly the point FRIDGE-1075's vetted-out files
   * do, so the row falls through to its next honest image — or to nothing,
   * which is what the FRIDGE-635 chain (photo → brand logo → house mark)
   * exists to render. Nothing is ever substituted for a dropped image.
   *
   * OPTIONAL AND INERT BY DEFAULT. Omitted (every caller that has not opted
   * in), this reduces to the `dropExcludedProductImages` call it replaced.
   *
   * NOT LOADED HERE. `resolveCanonicalImages` is synchronous and pure and the
   * set is a database read, so the caller loads it (`loadKnownDeadImageSet`,
   * service-role — routine_issues is internal per FRIDGE-1005) and passes it
   * in. That loader fails SOFT: a read error yields an empty set, which is
   * today's behaviour exactly.
   *
   * MATCHED AGAINST THE RAW STORED URL, which is why the drop belongs in the
   * precedence rather than after the emit. The set holds each URL raw and, for
   * a storage object, in its ABSOLUTE fridge.com form; the emitted display form
   * of a mirrored image is the SITE-RELATIVE `/product-images/<file>` path,
   * which is neither — so a post-emit filter misses mirrored images and a
   * pre-emit one does not.
   */
  knownDeadImages?: ReadonlySet<string>
}

/**
 * The canonical image set for a product row, RAW (as stored, before any
 * host proxying) — the one precedence every product surface shares
 * (FRIDGE-127 / FRIDGE-254 / FRIDGE-349):
 *
 *   1. `images_supabase` — the fridge.com-mirrored array, when the migration
 *      has actually put something there. It wins OUTRIGHT: a `partial` row has
 *      fewer mirrored images, not fake ones, and appending the retailer
 *      leftovers behind them would put a hotlink back on the page — the exact
 *      thumbnail that breaks when the retailer deletes the file.
 *   2. Otherwise, any already-mirrored entry sitting inside `images` (a storage
 *      URL that was written into the raw column) — ahead of the retailer ones.
 *      Zero rows are in that state today (checked live 08/17/2026: 0 of 31,419
 *      rows hold a storage URL in `images` that is missing from
 *      `images_supabase`), so this rung is defensive, not load-bearing.
 *   3. Only then the raw retailer/manufacturer URLs — the honest fallback for a
 *      product nothing has mirrored yet.
 *
 * The test is "the mirrored array is non-empty", not
 * `images_migration_status === 'migrated'`: a row only ever holds mirrored URLs
 * that were really uploaded, so a non-empty array is real regardless of the
 * status label. Checked live 08/16/2026 — of 31,419 product rows, every one
 * with a non-empty `images_supabase` is `migrated` (4,508) or `partial` (1),
 * and every `pending` / `failed` / `skipped` row has an empty one.
 *
 * Blank entries are dropped and duplicates collapse (first occurrence wins) —
 * a blank string in the mirrored array must never beat a real image, and a
 * gallery must never show the same photo twice.
 *
 * Real-or-Hidden: a product with neither array keeps an empty set. Nothing here
 * fabricates a URL, and no caller is handed a placeholder dressed as a photo.
 *
 * FRIDGE-1666: Amazon's images never leave here. The Amazon Associates licence
 * forbids storing a copy of an Amazon image and allows Amazon's own image link
 * only while current and linked to the Amazon page, which no card, gallery,
 * schema block or sitemap entry is. So every stored copy of an Amazon image (by
 * the mirror's position rule) and every stored Amazon address is dropped
 * (`amazonOriginImages`). A mirrored set still wins outright when every copy in
 * it was Amazon's: the row then has no photo of ours, and the retailer leftovers
 * are not put back on the page. The product page shows Amazon's own current
 * image link in that case, linked to the Amazon offer.
 *
 * Callers that render or emit URLs want `pickProductImages` / `pickProductImage`
 * below, which apply this precedence AND front every storage URL to a fridge.com
 * path. This raw form exists for callers that need the stored URL itself
 * (liveness probes, migrations, the PDP gallery resolver that proxies at its own
 * emit boundary).
 */
export function resolveCanonicalImages(
  row: ProductImageColumns | null | undefined,
  opts: ResolveCanonicalImagesOptions = {}
): string[] {
  if (!row) return []
  // FRIDGE-1075: vetted-out files (banners, logo tiles, accessories, other
  // appliances) leave the set HERE, so every consumer of this precedence —
  // gallery, JSON-LD, cards, rails, image sitemap — drops them together.
  // FRIDGE-1127: a PROVEN-dead URL leaves at the same point, and ONLY for a
  // caller that passed a set. The membership test is spelled out rather than
  // imported from lib/seo/known-dead-images so this module — which nearly
  // every surface imports — keeps no dependency on a module that imports it
  // back (`getImagePublicUrl`); the two are pinned equal by
  // tests/pdp-known-dead-images-fridge-1127.test.ts.
  const knownDead = opts.knownDeadImages
  const drop = (list: string[]): string[] => {
    const kept = dropExcludedProductImages(list)
    return knownDead && knownDead.size > 0 ? kept.filter((u) => !knownDead.has(u)) : kept
  }
  const amazon = amazonOriginImages(row)
  const notAmazon = (list: string[]): string[] => (amazon.size > 0 ? list.filter((u) => !amazon.has(u)) : list)
  const mirrored = drop(cleanImageList(row.images_supabase))
  if (mirrored.length > 0) return correctProductLead(notAmazon(mirrored))
  const raw = notAmazon(dropExcludedProductSourceImages(drop(cleanImageList(row.images)), cleanImageList(row.images_supabase)))
  const alreadyMirrored = raw.filter(isStorageObjectUrl)
  if (alreadyMirrored.length === 0) return correctProductLead(raw)
  return correctProductLead([...alreadyMirrored, ...raw.filter((u) => !isStorageObjectUrl(u))])
}

/**
 * The single canonical image for a product row — `images_supabase[0] ?? images[0]`,
 * null when the row genuinely has neither. RAW; see `pickProductImage` for the
 * display/emit-ready form.
 */
export function resolveCanonicalImage(row: ProductImageColumns | null | undefined): string | null {
  return resolveCanonicalImages(row)[0] ?? null
}

/**
 * The same precedence across SEVERAL rows that describe one appliance — the
 * `products` row and its `canonical_products` row (FRIDGE-362).
 *
 * A compare or enrichment surface holds both, and the rule that matters is
 * "mirrored anywhere beats hotlinked anywhere":
 *
 *   1. the first row that has a real mirrored set wins outright;
 *   2. only if NO row is mirrored does the first row with any image at all
 *      supply the set — hotlinked, honestly, exactly as `resolveCanonicalImages`
 *      would have returned it on its own.
 *
 * Sets are never merged. A gallery that mixed one product's mirrored photos
 * with another row's retailer leftovers would put a hotlink back on the page
 * for a product Fridge.com already hosts, which is the whole defect this
 * selector exists to prevent; and merging two rows' arrays could show the same
 * appliance twice from two feeds.
 *
 * `canonical_products` gains `images_supabase` in
 * `supabase/migrations/20260817170000_canonical_products_images_supabase.sql`;
 * until that lands the canonical row simply contributes its raw `images`, which
 * is what those surfaces already do — so adopting this function is safe before
 * the column exists and correct after it.
 */
export function resolveCanonicalImagesFrom(
  rows: ReadonlyArray<ProductImageColumns | null | undefined>,
  opts: ResolveCanonicalImagesOptions = {}
): string[] {
  // FRIDGE-1127: `opts` is forwarded so this twin cannot silently ignore a set
  // its single-row sibling honours. No caller passes one today.
  // A canonical twin can carry the same raw source without the identifying mirror.
  // Apply the narrowly scoped exclusions across all rows for this one appliance.
  const mirrors = rows.flatMap((row) => cleanImageList(row?.images_supabase))
  const resolved = rows.map((r) => ({ row: r, images: dropExcludedProductSourceImages(resolveCanonicalImages(r, opts), mirrors) }))
  for (const { row, images } of resolved) {
    if (images.length > 0 && cleanImageList(row?.images_supabase).length > 0) return images
  }
  for (const { images } of resolved) {
    if (images.length > 0) return images
  }
  return []
}

/**
 * Display/emit-ready form of `resolveCanonicalImagesFrom` — the multi-row twin
 * of `pickProductImages`, same options, same host rules.
 */
export function pickProductImagesFrom(
  rows: ReadonlyArray<ProductImageColumns | null | undefined>,
  opts: PickProductImagesOptions = {}
): string[] {
  return emitImages(resolveCanonicalImagesFrom(rows, opts), opts)
}

/** The single display/emit-ready image across several rows for one appliance. */
export function pickProductImageFrom(
  rows: ReadonlyArray<ProductImageColumns | null | undefined>,
  opts: PickProductImagesOptions = {}
): string | null {
  return pickProductImagesFrom(rows, opts)[0] ?? null
}

export interface PickProductImagesOptions extends ResolveCanonicalImagesOptions {
  /**
   * `false` (default) — site-relative display paths: a mirrored image comes back
   * as `/product-images/<file>` (same-origin; the next.config rewrite serves the
   * bytes; `next/image`, SafeImage and the RSC flight payload all carry a
   * fridge.com path and never a `*.supabase.co` host). The form for `<img>`
   * src, card payloads, API responses and anything a client component renders.
   *
   * `true` — absolute `https://fridge.com/product-images/<file>` URLs, for
   * contexts an EXTERNAL parser reads: image sitemaps, JSON-LD `image`,
   * og:image. (`getImagePublicUrl` does the fronting, so the host rule lives in
   * one place.)
   *
   * In both modes a retailer/manufacturer URL — reached only when nothing is
   * mirrored — passes through untouched: it is already absolute, and wrapping
   * it in `/_next/image` would make the optimizer a proxy for a host we do not
   * own (FRIDGE-156).
   */
  absolute?: boolean
  /** Override the canonical host for `absolute` mode (non-prod previews). */
  host?: string
}

/**
 * THE shared product-image selector — the display/emit-ready form of
 * `resolveCanonicalImages`. Every consumer of `products.images` routes through
 * this (or `pickProductImage` for a single card thumbnail): the sitemap, the
 * collection/hub/filter data layers, the recommendation engine, the API routes,
 * the blog related-product matchers and the JSON-LD builders. One author for
 * "which image, on which host" — the fifty-two files that selected `images`
 * alone and never `images_supabase` (measured 08/16/2026) each rendered the
 * retailer URL for a product Fridge.com already hosts, and the image sitemap
 * submitted those retailer URLs to Google Images.
 *
 * Contract: mirrored first, already-mirrored second, retailer last (see
 * `resolveCanonicalImages`); every storage URL fronted to a fridge.com path; a
 * product with no image at all returns `[]` — never a placeholder.
 */
export function pickProductImages(
  row: ProductImageColumns | null | undefined,
  opts: PickProductImagesOptions = {}
): string[] {
  return emitImages(resolveCanonicalImages(row, opts), opts)
}

/**
 * The host rule, in one place: a storage URL is fronted onto fridge.com (a
 * site-relative path, or an absolute URL for external parsers); a retailer URL
 * passes through untouched. Shared by the single-row and multi-row selectors so
 * the two can never emit a different host for the same stored URL.
 */
function emitImages(canonical: string[], opts: PickProductImagesOptions): string[] {
  if (canonical.length === 0) return []
  if (opts.absolute) {
    return canonical
      .map((u) => (isStorageObjectUrl(u) ? getImagePublicUrl(u, { host: opts.host }) : u))
      .filter((u): u is string => typeof u === 'string' && u.length > 0)
  }
  return canonical.map((u) => getProxiedStoragePath(u))
}

/**
 * The single display/emit-ready image for a product row — `pickProductImages(row)[0]`,
 * null when the row genuinely has none. What a product card, a rail thumbnail,
 * a comparison column or an og:image needs.
 */
export function pickProductImage(
  row: ProductImageColumns | null | undefined,
  opts: PickProductImagesOptions = {}
): string | null {
  return pickProductImages(row, opts)[0] ?? null
}

/**
 * "Does this product carry a real photo?" — the gate the rails and grids apply
 * before rendering a card. Accepts an absolute http(s) URL OR a site-relative
 * proxied storage path (`/product-images/<file>` and the other proxied
 * buckets), which is what `pickProductImages` returns for a mirrored image.
 * The older inline `/^https?:/` tests silently dropped every mirrored product
 * from a rail once the data layer started returning fridge.com paths.
 */
export interface ImageUrlOptions {
  /** Width hint for the optimizer. Defaults to 1200 (good for OG images). */
  width?: number;
  /** Quality — must be one of the values in next.config.js `images.qualities` (currently [75, 90]). Defaults to 90 (high). */
  quality?: number;
  /** Override the canonical host. Useful for non-prod previews. */
  host?: string;
}

/**
 * For <Image> components rendering inside a fridge.com page.
 *
 * A proxied-bucket Storage URL (incl. product-images) is rewritten to its
 * site-relative `/<bucket>/<path>` so that when Next.js pipes it through
 * `/_next/image?url=...`, the query param carries a same-origin path — never a
 * raw `*.supabase.co` host (owner directive 07/20). The next.config rewrite
 * serves the bytes.
 *
 * Any other input (external retailer/manufacturer CDN URL, already-relative
 * path) is returned unchanged — `getProxiedStoragePath` only touches proxied
 * Storage URLs, so external hosts keep flowing through the optimizer as before.
 */
export function getImageDisplayUrl(supabaseUrl: string | null | undefined): string | null {
  if (!supabaseUrl) return null;
  return getProxiedStoragePath(supabaseUrl);
}

/**
 * For OG meta tags + schema.org JSON-LD + any other context where the URL
 * is consumed by an external parser.
 *
 * Returns a fridge.com `_next/image` URL that proxies the Supabase Storage
 * image. External parsers see fridge.com as the image host — no raw
 * `*.supabase.co` URL leaks.
 *
 * Examples:
 *   getImagePublicUrl('https://abc.supabase.co/storage/v1/object/public/product-images/foo.jpg')
 *     → 'https://fridge.com/_next/image?url=https%3A%2F%2Fabc.supabase.co%2Fstorage%2Fv1%2Fobject%2Fpublic%2Fproduct-images%2Ffoo.jpg&w=1200&q=85'
 *
 *   getImagePublicUrl('https://fridge.com/already-our-host.png')
 *     → returned as-is (already on fridge.com)
 */
export function getImagePublicUrl(
  supabaseUrl: string | null | undefined,
  opts: ImageUrlOptions = {}
): string | null {
  if (!supabaseUrl) return null;

  const host = opts.host ?? FRIDGE_HOST;
  const width = opts.width ?? 1200;
  const quality = opts.quality ?? 90;

  // Proxied brand-asset bucket? Emit the absolute fridge.com/<bucket>/<path>
  // URL directly — the next.config rewrite serves it, so external parsers see
  // fridge.com with no `/_next/image?url=…supabase…` query leaking the storage
  // host at all (cleaner than the optimizer path, and correct for JSON-LD /
  // OpenGraph where an absolute URL is required).
  const proxied = parseProxiedStorageUrl(supabaseUrl);
  if (proxied) {
    return `${host}/${proxied.rest}`;
  }

  // Already a site-relative proxied path (`/product-images/<file>` — what
  // `pickProductImages` / `getProxiedStoragePath` hand to display surfaces)?
  // An external parser needs it absolute: same host rule as above.
  if (supabaseUrl.startsWith('/')) {
    const bucket = supabaseUrl.replace(/^\/+/, '').split(/[/?#]/)[0];
    if ((PROXIED_STORAGE_BUCKETS as readonly string[]).includes(bucket)) {
      return `${host}/${supabaseUrl.replace(/^\/+/, '')}`;
    }
  }

  // Already on our host? Pass through unchanged.
  try {
    const u = new URL(supabaseUrl);
    if (u.hostname === 'fridge.com' || u.hostname === 'www.fridge.com') {
      return supabaseUrl;
    }
  } catch {
    // Not a parseable URL — return as-is rather than crash
    return supabaseUrl;
  }

  return `${host}/_next/image?url=${encodeURIComponent(supabaseUrl)}&w=${width}&q=${quality}`;
}

/**
 * For hosted manufacturer PDFs (canonical_products.pdfs supabase_url).
 *
 * PDFs must NOT go through getImagePublicUrl / `/_next/image`: the Next image
 * optimizer only handles raster images and returns HTTP 400 for a PDF
 * (observed on the Manuals & Guides links, owner report 2026-07-08 — the
 * earlier "returns application/pdf as-is" assumption was wrong).
 *
 * Instead we map the storage object to a fridge.com `/manuals/<path>` URL
 * that a next.config.js rewrite proxies straight to Supabase Storage — the
 * body is served untouched (application/pdf), and no raw `*.supabase.co` host
 * leaks (Rule 4). Non-Supabase or unrecognized inputs return null so the
 * caller can fall back to the external href.
 *
 *   getHostedPdfUrl('https://abc.supabase.co/storage/v1/object/public/product-pdfs/FIN-1/hash.pdf')
 *     → '/manuals/FIN-1/hash.pdf'
 */
export function getHostedPdfUrl(supabaseUrl: string | null | undefined): string | null {
  if (!supabaseUrl) return null;
  const marker = '/storage/v1/object/public/product-pdfs/';
  const idx = supabaseUrl.indexOf(marker);
  if (idx === -1) return null;
  const path = supabaseUrl.slice(idx + marker.length).replace(/^\/+/, '');
  if (!path) return null;
  return `/manuals/${path}`;
}

/**
 * Build the Supabase Storage public URL for a given path within the
 * product-images bucket. Useful when callers have just the path (no full URL).
 *
 *   buildSupabaseProductImageUrl('<project-ref>', 'fridge-com-whirlpool-wrt519szdw-1.jpg')
 *     → 'https://<project-ref>.supabase.co/storage/v1/object/public/product-images/fridge-com-whirlpool-wrt519szdw-1.jpg'
 */
export function buildSupabaseProductImageUrl(supabaseProjectRef: string, path: string): string {
  const cleanPath = path.replace(/^\/+/, '');
  return `https://${supabaseProjectRef}.supabase.co/storage/v1/object/public/product-images/${cleanPath}`;
}

/**
 * Slugify a brand name for use in image filenames. Mirrors the SQL
 * `slugify_brand_name()` function so the Phase 3 backfill produces filenames
 * the rest of the system recognizes.
 */
export function slugifyForFilename(input: string | null | undefined): string {
  if (!input) return '';
  return input
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/**
 * Build the canonical filename for a product image upload.
 * Format (LOCKED per Phase 3): `fridge-com-{brand-slug}-{model-slug}-{idx}.{ext}`
 *
 *   buildProductImageFilename('Whirlpool', 'WRT519SZDW', 3, 'jpg')
 *     → 'fridge-com-whirlpool-wrt519szdw-3.jpg'
 */
export function buildProductImageFilename(
  brand: string,
  model: string,
  idx: number,
  ext: string
): string {
  const brandSlug = slugifyForFilename(brand);
  const modelSlug = slugifyForFilename(model);
  const safeExt = ext.replace(/^\./, '').toLowerCase();
  // 1-based index — feels natural in URLs ("...-1.jpg" not "...-0.jpg")
  return `fridge-com-${brandSlug}-${modelSlug}-${idx}.${safeExt}`;
}

/**
 * Detect a sensible file extension from an image MIME type.
 * Used by the migration routine when uploading downloaded images.
 */
export function extensionFromMimeType(mime: string | null | undefined): string {
  if (!mime) return 'jpg';
  const m = mime.toLowerCase().split(';')[0].trim();
  switch (m) {
    case 'image/jpeg': return 'jpg';
    case 'image/png': return 'png';
    case 'image/webp': return 'webp';
    case 'image/gif': return 'gif';
    default: return 'jpg';
  }
}

/**
 * Rewrite every proxied-bucket Supabase Storage URL inside an arbitrary
 * serializable value (string, array, object) to its site-relative
 * `/<bucket>/<path>` proxy path — same bytes, our host. Non-proxied-bucket
 * URLs and everything else pass through untouched. Used by content surfaces
 * (blog articles) whose payloads carry storage URLs baked into many nested
 * fields — a single deep pass keeps the RSC payload, preloads, srcsets, and
 * schema all on fridge.com paths (Rule 4 / owner directive 07/20).
 */
export function proxySupabaseUrlsDeep<T>(value: T): T {
  const re = /https?:\/\/[a-z0-9-]+\.supabase\.co\/storage\/v1\/object\/public\/([a-z0-9-]+)\//g;
  const rewriteString = (s: string): string =>
    s.replace(re, (full, bucket) =>
      (PROXIED_STORAGE_BUCKETS as readonly string[]).includes(bucket) ? `/${bucket}/` : full
    );
  const walk = (v: unknown): unknown => {
    if (typeof v === 'string') return rewriteString(v);
    if (Array.isArray(v)) return v.map(walk);
    if (v && typeof v === 'object') {
      const out: Record<string, unknown> = {};
      for (const [k, val] of Object.entries(v as Record<string, unknown>)) out[k] = walk(val);
      return out;
    }
    return v;
  };
  return walk(value) as T;
}
